import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import SellIcon from "@mui/icons-material/Sell";

type TagInputProps = {
  className?: string;
  tags?: string[];
  viewOnly?: boolean;
  helperText?: string;
  /** Defaults to "Add new tag". A page that drops the helper text on a phone can carry its example here. */
  placeholder?: string;
  size?: "small" | "medium";
  onTagsUpdated?: (tags: string[]) => void;
  onTagRemoved?: (tag: string) => void;
};

export default function TagInput(props: Readonly<TagInputProps>) {
  const { className, tags, viewOnly, helperText, size, onTagsUpdated, onTagRemoved } = props;
  const placeholder = props.placeholder ?? "Add new tag";

  const [tagInputOpen, setTagInputOpen] = useState<boolean>(false);
  const [pendingTag, setPendingTag] = useState<string>("");

  /**
   * Keeps a tag someone typed but never pressed Enter on (issue #5111).
   *
   * `clearOnBlur` threw that text away, so a tag typed and then followed straight by Save was
   * simply gone — and nothing had said it would not count. There is no commit step for tags any
   * more than for the name beside them: leaving the field adds what is in it, and the form's own
   * Save is the commit.
   */
  function absorbPendingTag(): void {
    const trimmed = pendingTag.trim();
    setPendingTag("");
    if (!trimmed) return;
    if (tags?.includes(trimmed)) return;
    onTagsUpdated?.([...(tags ?? []), trimmed]);
  }

  return (
    <Autocomplete
      className={className}
      open={tagInputOpen}
      onOpen={() => !viewOnly && setTagInputOpen(true)}
      onClose={() => setTagInputOpen(false)}
      multiple
      disabled={viewOnly}
      options={[]}
      freeSolo
      filterOptions={(options, params) => params.inputValue === '' ? [] : [params.inputValue]}
      getOptionLabel={(option) => `Add "${option}" as new tag`}
      blurOnSelect
      inputValue={pendingTag}
      onInputChange={(_event, value) => setPendingTag(value)}
      renderValue={(selected) => (
        <>
          {selected.map((option) => (
            <Chip
              key={option}
              title="Click to remove tag"
              label={option}
              onDelete={() => {
                onTagRemoved?.(option);
              }}
              sx={{ marginRight: 0.5 }}
            />
          ))}
        </>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: { 
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {!tags?.length && <SellIcon color="disabled" />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.slotProps.htmlInput,
              readOnly: viewOnly,
            }
          }}
          label="Tags"
          placeholder={placeholder}
          helperText={helperText}
          size={size}
          onBlur={absorbPendingTag}
        />
      )}
      value={tags ?? []}
      onChange={(_event, value) => {
        // Selecting the "Add …" option is what clears the box; anything left in it after a change
        // is text for the next tag, not the one just added.
        setPendingTag("");
        onTagsUpdated?.(value);
      }}
    />
  );
}
